import { ValidationError } from '@domain/errors';
import type { Offer, OfferDraft } from '@domain/entities';
import type {
  NotificationRepository,
  OfferCreateInput,
  OfferQuery,
  OfferRepository,
} from '@domain/repositories';
import type { NotificationType, OfferStatus, Page } from '@domain/value-objects';
import type { AccessPolicy } from '../../ports';
import type { UseCase } from '../use-case';

export class CreateOffer implements UseCase<OfferCreateInput, Offer> {
  constructor(private readonly offers: OfferRepository) {}

  async execute(input: OfferCreateInput): Promise<Offer> {
    if (!input.title.trim()) throw new ValidationError('Formulaire incomplet', { title: 'Le titre est requis' });
    if (!input.businessId) throw new ValidationError('Formulaire incomplet', { businessId: 'Le propriétaire est requis' });
    if (!input.pricing) throw new ValidationError('Formulaire incomplet', { pricing: 'La formule est requise' });
    return this.offers.create(input);
  }
}

export class SearchOffers implements UseCase<OfferQuery, Page<Offer>> {
  constructor(private readonly repo: OfferRepository) {}
  execute(query: OfferQuery): Promise<Page<Offer>> {
    return this.repo.search(query);
  }
}

export class GetOffer implements UseCase<string, Offer> {
  constructor(private readonly repo: OfferRepository) {}
  execute(id: string): Promise<Offer> {
    return this.repo.findById(id);
  }
}

export interface ModerateOffersInput {
  ids: readonly string[];
  status: Extract<OfferStatus, 'active' | 'rejected'>;
}

/**
 * Approbation / rejet d'offres. Déclenche systématiquement la notification
 * à l'entreprise, conformément au gabarit paramétré.
 */
export class ModerateOffers implements UseCase<ModerateOffersInput, Offer[]> {
  constructor(
    private readonly offers: OfferRepository,
    private readonly notifications: NotificationRepository,
  ) {}

  async execute({ ids, status }: ModerateOffersInput): Promise<Offer[]> {
    if (ids.length === 0) {
      throw new ValidationError('Aucune offre sélectionnée');
    }
    const updated = await this.offers.setStatus(ids, status);
    const type: NotificationType = status === 'active' ? 'offerApproved' : 'offerRejected';
    const templates = await this.notifications.getTemplates();
    await Promise.all(
      updated.map((offer) =>
        this.notifications.append(type, offer.company, templates[type].subject),
      ),
    );
    return updated;
  }
}

/** Suspension d'une offre active : réservé au super-admin. */
export class SuspendOffer implements UseCase<string, Offer> {
  constructor(
    private readonly offers: OfferRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<Offer> {
    this.policy.assertSuperAdmin('suspendre une offre');
    const [offer] = await this.offers.setStatus([id], 'suspended');
    return offer;
  }
}

export interface UpdateOfferInput {
  id: string;
  draft: OfferDraft;
}

export class UpdateOffer implements UseCase<UpdateOfferInput, Offer> {
  constructor(private readonly offers: OfferRepository) {}

  async execute({ id, draft }: UpdateOfferInput): Promise<Offer> {
    if (!draft.title.trim()) {
      throw new ValidationError('Formulaire incomplet', { title: 'Le titre est requis' });
    }
    return this.offers.update(id, { ...draft, title: draft.title.trim() });
  }
}

export class DeleteOffer implements UseCase<string, void> {
  constructor(
    private readonly offers: OfferRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer une offre');
    await this.offers.remove(id);
  }
}
